import { Drawer } from '@mui/material';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { useUsersListQuery } from 'api';
import { Spinner } from 'components';
import { CreateDocumentForm } from 'features/documents/components';
import {
  RegisterEquipmentForm,
  RequestEquipmentForm,
  Tabs,
  TabPanel,
} from 'features/equipments/components';
import { createEquipmentsTabs } from 'features/equipments/components/constants';
import { defaultValuesAdapter as equipmentDefaultValues } from 'features/equipments/containers/CreateEquipmentPage/adapter';
import { CreateNotificationForm } from 'features/notificationsCustom/components';
import { CreatePollForm } from 'features/poll/components';
import { defaultValuesAdapter as pollDefaultValues } from 'features/poll/containers/CreatePollPage/adapter';
import { CreateTaskForm } from 'features/tasks/components';
import { defaultValuesAdapter as taskDefaultValues } from 'features/tasks/containers/CreateTaskPage/adapter';
import { useAuth, useCreateDraft, useSubscription, useWindowSize } from 'hooks';
import { Links } from 'settings';
import { EquipmentTabsValues, UserStatus } from 'types';

import { CREATE_ACTIONS_CONFIG, MANAGE_ACTIONS_CONFIG } from './config';
import {
  ActionDescription,
  ActionIconWrapper,
  ActionItem,
  ActionLabel,
  ActionsList,
  ActionTextWrapper,
  ButtonW,
  ContentW,
  SectionTitle,
  TooltipW,
} from './styled';

type ActionEntry = { label: string; description: string; icon: ReactElement; link: string };

export function ActionOptionsList(): ReactElement {
  const { t } = useTranslation();
  const { isManager } = useAuth();
  const { isTrial } = useSubscription();
  const { isMobile } = useWindowSize();

  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isPollModalOpen, setIsPollModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isEquipmentModalOpen, setIsEquipmentModalOpen] = useState(false);
  const [equipmentTab, setEquipmentTab] = useState<EquipmentTabsValues>(
    EquipmentTabsValues.REGISTER,
  );

  const { draft: documentDraft, isLoading: isDocumentDraftLoading } = useCreateDraft(
    isMobile && isDocumentModalOpen,
  );
  const { draft: taskDraft, isLoading: isTaskDraftLoading } = useCreateDraft(
    isMobile && isTaskModalOpen,
  );
  const { draft: pollDraft, isLoading: isPollDraftLoading } = useCreateDraft(
    isMobile && isPollModalOpen,
  );
  const { draft: equipmentDraft, isLoading: isEquipmentDraftLoading } = useCreateDraft(
    isMobile && isEquipmentModalOpen,
  );

  const { isLoading: isUsersListLoading, usersList } = useUsersListQuery({
    status: [UserStatus.Active, UserStatus.Registered],
  });

  const taskDefaults = taskDefaultValues({ draft: taskDraft });
  const equipmentDefaults = (isDefaultEditable: boolean) =>
    equipmentDefaultValues({ isDefaultEditable, draft: equipmentDraft ?? undefined });

  const drawerPaperSx = {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    height: '92vh',
    overflowX: 'hidden',
    overflowY: 'auto',
  };

  const renderActions = (actions: Record<string, ActionEntry>) =>
    Object.values(actions).map(({ icon, label, link, description }) => {
      const isBlocked =
        isTrial && (link === Links.actions.employeesInvite || link === Links.actions.offboarding);

      const modalOpener = isMobile
        ? link === Links.actions.createPost
          ? () => setIsDocumentModalOpen(true)
          : link === Links.actions.createTask
          ? () => setIsTaskModalOpen(true)
          : link === Links.actions.createPoll
          ? () => setIsPollModalOpen(true)
          : link === Links.actions.createNotification
          ? () => setIsNotificationModalOpen(true)
          : link === Links.actions.createEquipment
          ? () => setIsEquipmentModalOpen(true)
          : null
        : null;

      const content = isTrial ? (
        <TooltipW
          title={t(
            link === Links.actions.employeesInvite
              ? 'blockedOnboardingHint'
              : link === Links.actions.offboarding
              ? 'blockedOffboardingHint'
              : '',
          )}
        >
          <ActionItem isBlocked={isBlocked}>
            <ActionIconWrapper>{icon}</ActionIconWrapper>
            <ActionTextWrapper>
              <ActionLabel>{label}</ActionLabel>
              {description && <ActionDescription>{description}</ActionDescription>}
            </ActionTextWrapper>
          </ActionItem>
        </TooltipW>
      ) : (
        <ActionItem isBlocked={isBlocked}>
          <ActionIconWrapper>{icon}</ActionIconWrapper>
          <ActionTextWrapper>
            <ActionLabel>{label}</ActionLabel>
            {description && <ActionDescription>{description}</ActionDescription>}
          </ActionTextWrapper>
        </ActionItem>
      );

      return (
        <ButtonW key={label} color="info">
          {modalOpener ? (
            <button
              onClick={modalOpener}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                width: '100%',
                cursor: 'pointer',
              }}
            >
              {content}
            </button>
          ) : (
            <NavLink to={isBlocked ? '' : link} style={{ textDecoration: 'none', width: '100%' }}>
              {content}
            </NavLink>
          )}
        </ButtonW>
      );
    });

  return (
    <ContentW>
      <SectionTitle>{t('actions.sections.create')}</SectionTitle>
      <ActionsList>
        {renderActions(CREATE_ACTIONS_CONFIG(isManager) as Record<string, ActionEntry>)}
      </ActionsList>

      <SectionTitle>{t('actions.sections.manage')}</SectionTitle>
      <ActionsList>
        {renderActions(MANAGE_ACTIONS_CONFIG(isManager) as Record<string, ActionEntry>)}
      </ActionsList>

      {isMobile && (
        <>
          {/* Document modal */}
          <Drawer
            anchor="bottom"
            open={isDocumentModalOpen}
            onClose={() => setIsDocumentModalOpen(false)}
            PaperProps={{ sx: drawerPaperSx }}
          >
            {isDocumentDraftLoading ? (
              <Spinner />
            ) : (
              <CreateDocumentForm
                draft={documentDraft ?? undefined}
                isEdit={false}
                type="post"
                onClose={() => setIsDocumentModalOpen(false)}
              />
            )}
          </Drawer>

          {/* Task modal */}
          <Drawer
            anchor="bottom"
            open={isTaskModalOpen}
            onClose={() => setIsTaskModalOpen(false)}
            PaperProps={{ sx: drawerPaperSx }}
          >
            {isTaskDraftLoading || isUsersListLoading || !taskDraft ? (
              <Spinner />
            ) : (
              <CreateTaskForm
                draftId={taskDraft.id}
                defaultValues={taskDefaults}
                usersList={usersList}
                onClose={isMobile ? () => setIsTaskModalOpen(false) : undefined}
              />
            )}
          </Drawer>

          {/* Poll modal */}
          <Drawer
            anchor="bottom"
            open={isPollModalOpen}
            onClose={() => setIsPollModalOpen(false)}
            PaperProps={{ sx: drawerPaperSx }}
          >
            {isPollDraftLoading ? (
              <Spinner />
            ) : (
              <CreatePollForm
                draft={pollDraft}
                defaultValues={pollDefaultValues}
                onClose={() => setIsPollModalOpen(false)}
              />
            )}
          </Drawer>

          {/* Notification modal */}
          <Drawer
            anchor="bottom"
            open={isNotificationModalOpen}
            onClose={() => setIsNotificationModalOpen(false)}
            PaperProps={{ sx: drawerPaperSx }}
          >
            {isUsersListLoading ? (
              <Spinner />
            ) : (
              <CreateNotificationForm
                usersList={usersList}
                onClose={() => setIsNotificationModalOpen(false)}
              />
            )}
          </Drawer>

          {/* Equipment modal */}
          <Drawer
            anchor="bottom"
            open={isEquipmentModalOpen}
            onClose={() => setIsEquipmentModalOpen(false)}
            PaperProps={{ sx: drawerPaperSx }}
          >
            {isEquipmentDraftLoading || isUsersListLoading ? (
              <Spinner />
            ) : (
              <>
                <Tabs
                  activeTab={equipmentTab}
                  tabs={createEquipmentsTabs}
                  handleChange={(_, val) => setEquipmentTab(val)}
                />
                <TabPanel path={EquipmentTabsValues.REGISTER} value={equipmentTab}>
                  <RegisterEquipmentForm
                    draft={equipmentDraft}
                    usersList={usersList}
                    defaultValues={equipmentDefaults}
                    onClose={() => setIsEquipmentModalOpen(false)}
                  />
                </TabPanel>
                <TabPanel path={EquipmentTabsValues.REQUEST} value={equipmentTab}>
                  <RequestEquipmentForm
                    draft={equipmentDraft}
                    usersList={usersList}
                    defaultValues={equipmentDefaults}
                    onClose={() => setIsEquipmentModalOpen(false)}
                  />
                </TabPanel>
              </>
            )}
          </Drawer>
        </>
      )}
    </ContentW>
  );
}
